import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  AtSymbolIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  NewspaperIcon,
  PhoneIcon,
  UserCircleIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/solid';
import { Button } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Company } from '../../../../@types/Company';
import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import { useAdsModal } from '../../../../hooks/useAdsModal';
import { useReferentSendAvailability } from '../../../../hooks/useReferentSendAvailability';
import {
  setContact,
  updateOwner,
} from '../../../../store/DpeOwnerReducer';
import { setCurrentStep } from '../../../../store/DpeReducer';
import type { RootState } from '../../../../store/store';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { generateOwnerLetterDocx } from '../../../../utils/ownerLetterDocx';
import { DJ_URL } from '../../../../utils/routes';
import AdsModal from '../../../common/AdsModal/AdsModal';
import MUITable from '../../../common/MUITable';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup';
import CoproLocalOwned from '../../components/CoproLocalOwned';
import DpeDetailsPopup from '../../components/DpeDetailsPopup';
import { DPE_QUALIF_STEPS } from '../../components/DpeStepsQualif/dpeQualifStep';
import DpeOwnerForm from '../../components/DpeStepsQualif/DpeStep2/DpeOwnerForm.module';
import OwnerInfos from '../../components/OwnerInfos';
import { formatDpeDetailsFromOwner } from '../../utils/formatters';
import {
  FIRST_CONTACT_TEMPLATE,
  FOLLOW_UP_TEMPLATE,
  generateDpeEmailContent,
  generateFollowUpEmailContent,
} from '../utils/emailContentGenerator';
import { handleContactClick as registerContactIntent } from '../utils/OwnerCommunicationService';
import EmailPreview from './EmailPreview';

const FOLLOW_UP_DAYS_THRESHOLD = 7;


const headers = [
  { title: 'Propriétaire' },
  { title: 'Propriété' },
  { title: 'DPE' },
  { title: 'Dernier contact' },
  {
    title: 'Actions',
    customContentClass: 'align-top py-2 leading-normal',
  },
];

interface PendingTableProps {
  data: Owner[];
  onOwnerEdit?: (ownerId: number) => void;
  apiPagination?: boolean;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onRefresh?: () => void;
}

const PendingTable: React.FC<PendingTableProps> = ({ 
  data,
  onOwnerEdit = () => {}, 
  apiPagination = false,
  totalCount,
  onPageChange,
  currentPage = 0,
  currentRowsPerPage = 10,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDpeDetails, setSelectedDpeDetails] = useState({});
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState({
    content: '',
    subject: '',
    to: '',
  });

  const [isFollowUpPreviewOpen, setIsFollowUpPreviewOpen] = useState(false);
  const [followUpPreviewData, setFollowUpPreviewData] = useState<{
    content: string;
    subject: string;
    to: string;
    ownerId: number | null;
    canSendFromReferent: boolean;
  }>({
    content: '',
    subject: '',
    to: '',
    ownerId: null,
    canSendFromReferent: false,
  });

  const { handleAdsClick, adsModalProps } = useAdsModal();
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string | null>(null);
  const { canOwnerSendFromReferent, getOwnerCompany, getOwnerPrivacyPolicyLink } = useReferentSendAvailability(company?.id);

  useEffect(() => {
    const fetchPrivacyPolicyLink = async() => {
      if (!company?.siren || !jwt) {
        return;
      }

      try {
        const response = await fetch(`/api/public/dj/companies/${company.siren}/privacy-policy`, JWTHeader(jwt));
        
        if (response.ok) {
          const privacyPolicyData = await response.json();
          if (privacyPolicyData.link) {
            setPrivacyPolicyLink(`${DJ_URL}${privacyPolicyData.link}`);
          }
        }
      } catch (error) {
        console.error('[PendingTable] Error fetching privacy policy link:', error);
      }
    };

    fetchPrivacyPolicyLink();
  }, [company?.siren, jwt]);

  const handleEditClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsEditFormOpen(true);
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS));
  };

  const handleEditClose = () => {
    setIsEditFormOpen(false);
    setSelectedOwner(null);
    dispatch(setCurrentStep(null));
  };

  const handleDetailClick = (dpeDetails) => {
    setSelectedDpeDetails(dpeDetails);
    setIsModalOpen(true);
  };

  const handleViewEmailContent = async(owner: Owner) => {
    if (!owner.email) {return;}
    
    const { subject, content } = await generateDpeEmailContent(owner, getOwnerCompany(owner), getOwnerPrivacyPolicyLink(owner, privacyPolicyLink), undefined, jwt);

    setEmailPreviewData({
      content,
      subject,
      to: owner.email,
    });
    setIsEmailPreviewOpen(true);
  };

  const handleRedownloadCourrier = async(owner: Owner) => {
    try {
      showToast('info', 'Génération du courrier Word en cours...');
      await generateOwnerLetterDocx(owner, getOwnerCompany(owner), FIRST_CONTACT_TEMPLATE.REFERENT_ONLY, jwt);
      showToast('success', 'Courrier Word téléchargé avec succès !');
    } catch (error) {
      console.error('Error generating letter DOCX:', error);
      showToast('error', 'Erreur lors de la génération du courrier Word.');
    }
  };

  const getConsentContactDate = (owner: Owner): Date | null => {
    if (!owner.contacts || owner.contacts.length === 0) {
      return null;
    }
    const consentContacts = owner.contacts.filter(
      (c) => c.type === ContactInteractionType.MAIL_FOR_CONSENT || c.type === ContactInteractionType.PAPER_FOR_CONSENT,
    );
    if (consentContacts.length === 0) {
      return null;
    }
    const sorted = [...consentContacts].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return new Date(sorted[0].createdAt);
  };

  const isFollowUpEligible = (owner: Owner): boolean => {
    const consentDate = getConsentContactDate(owner);
    if (!consentDate) {
      return false;
    }
    const daysSinceContact = (Date.now() - consentDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceContact >= FOLLOW_UP_DAYS_THRESHOLD;
  };

  const hasFollowUpBeenSent = (owner: Owner): boolean => {
    if (!owner.contacts || owner.contacts.length === 0) {
      return false;
    }
    return owner.contacts.some((c) => c.type === ContactInteractionType.MAIL_FOLLOW_UP);
  };

  const handleFollowUpClick = async(owner: Owner) => {
    if (!owner.email) {return;}

    const { subject, content } = await generateFollowUpEmailContent(owner, getOwnerCompany(owner), getOwnerPrivacyPolicyLink(owner, privacyPolicyLink), FOLLOW_UP_TEMPLATE.STANDARD, jwt);

    setFollowUpPreviewData({
      content,
      subject,
      to: owner.email,
      ownerId: owner.id,
      canSendFromReferent: canOwnerSendFromReferent(owner),
    });
    setIsFollowUpPreviewOpen(true);
  };

  const handleFollowUpAction = async() => {
    if (followUpPreviewData.ownerId && jwt) {
      const contact = await registerContactIntent(followUpPreviewData.ownerId, ContactInteractionType.MAIL_FOLLOW_UP, jwt);
      if (contact) {
        contact.user = {
          id: currentUser.id,
          uuid: currentUser.uuid || '',
          firstname: currentUser.firstname,
          lastname: currentUser.lastname,
          email: currentUser.email,
          lastActivityAt: new Date().toISOString(),
          createdAt: currentUser.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        dispatch(setContact({ contact, ownerId: followUpPreviewData.ownerId }));

        if (onRefresh) {
          onRefresh();
        }
      }
    }
  };

  const handleFollowUpSendFromReferent = async() => {
    if (!followUpPreviewData.ownerId || !jwt) {
      showToast('error', 'Impossible d\'envoyer le mail : propriétaire non identifié.');
      throw new Error('Missing ownerId or jwt');
    }

    const requestInit = JWTHeader(jwt);
    requestInit.method = 'POST';
    requestInit.headers['Content-Type'] = 'application/json';
    requestInit.body = JSON.stringify({
      subject: followUpPreviewData.subject,
      content: followUpPreviewData.content,
    });

    const response = await fetch(`/api/owners/${followUpPreviewData.ownerId}/contacts/mail/send-from-referent`, requestInit);
    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      const defaultErrorMessage = 'Erreur lors de l\'envoi SMTP depuis le mail référent.';
      const apiErrorMessage = Array.isArray(responseData?.errors)
        ? responseData.errors[0]?.message
        : responseData?.message;
      const errorMessage = apiErrorMessage || defaultErrorMessage;
      showToast('error', errorMessage);
      throw new Error(errorMessage);
    }

    showToast('success', 'Email de relance envoyé depuis le mail référent.');
  };

  const handleFollowUpPreviewClose = () => {
    setIsFollowUpPreviewOpen(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getLastContactInfo = (owner: Owner) => {
    if (!owner.contacts || owner.contacts.length === 0) {
      return <div className="text-gray-500">Aucun contact</div>;
    }
    
    const sortedContacts = [...owner.contacts].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    
    const lastContact = sortedContacts[0];
    
    let contactIcon;
    switch (lastContact.type) {
    case ContactInteractionType.MAIL:
    case ContactInteractionType.MAIL_FOR_CONSENT:
    case ContactInteractionType.MAIL_FOLLOW_UP:
      contactIcon = <AtSymbolIcon className="mr-1 inline h-5 w-5 shrink-0 text-gray-600" />;
      break;
    case ContactInteractionType.PAPER:
    case ContactInteractionType.PAPER_FOR_CONSENT:
      contactIcon = <NewspaperIcon className="mr-1 inline h-5 w-5 shrink-0 text-gray-600" />;
      break;
    case ContactInteractionType.PHONE:
      contactIcon = <PhoneIcon className="mr-1 inline h-5 w-5 shrink-0 text-gray-600" />;
      break;
    case ContactInteractionType.VISIO:
      contactIcon = <VideoCameraIcon className="mr-1 inline h-5 w-5 shrink-0 text-gray-600" />;
      break;
    default:
      contactIcon = null;
    }
    
    const contactTypeMap = {
      [ContactInteractionType.MAIL]: 'Email',
      [ContactInteractionType.MAIL_FOR_CONSENT]: 'Email de consentement',
      [ContactInteractionType.MAIL_FOLLOW_UP]: 'Email de relance',
      [ContactInteractionType.PAPER]: 'Courrier',
      [ContactInteractionType.PAPER_FOR_CONSENT]: 'Courrier de consentement',
      [ContactInteractionType.PHONE]: 'Téléphone',
      [ContactInteractionType.VISIO]: 'Visio',
      [ContactInteractionType.PHYSICAL]: 'Présentiel',
    };
    
    const contactTypeLabel = contactTypeMap[lastContact.type] || lastContact.type.replaceAll('-', ' ');
    
    const hasUserInfo = lastContact.user && lastContact.user.firstname && lastContact.user.lastname;
    const userInfo = hasUserInfo ? 
      `${lastContact.user.firstname} ${lastContact.user.lastname}` : 
      'Utilisateur inconnu';
    
    const isEmailContact = lastContact.type === ContactInteractionType.MAIL || 
                           lastContact.type === ContactInteractionType.MAIL_FOR_CONSENT ||
                           lastContact.type === ContactInteractionType.MAIL_FOLLOW_UP;
    const isPaperContact = lastContact.type === ContactInteractionType.PAPER || 
                           lastContact.type === ContactInteractionType.PAPER_FOR_CONSENT;
    
    return (
      <div>
        <div className="flex items-center font-medium">
          {contactIcon}
          {contactTypeLabel}
        </div>
        <div className="mt-1 text-sm text-gray-600">{formatDate(lastContact.createdAt)}</div>
        <div className="mt-1 flex items-center text-sm">
          <UserCircleIcon className="mr-1 h-5 w-5 shrink-0 text-gray-500" />
          <span className="text-gray-700">{userInfo}</span>
        </div>
        
        {isEmailContact && owner.email && (
          <Button
            size="xs"
            onClick={() => handleViewEmailContent(owner)}
            className="mt-2 border border-gray-300 bg-gray-100 text-white hover:bg-gray-200"
            type="button"
          >
            <DocumentTextIcon className="mr-1 h-3 w-3 shrink-0" />
            Revoir le contenu du mail
          </Button>
        )}
        
        {isPaperContact && (
          <Button
            size="xs"
            onClick={() => handleRedownloadCourrier(owner)}
            className="mt-2 border border-gray-300 bg-gray-100 text-white hover:bg-gray-200"
            type="button"
          >
            <ArrowDownTrayIcon className="mr-1 h-3 w-3 shrink-0" />
            Télécharger de nouveau le courrier
          </Button>
        )}
      </div>
    );
  };

  const isCertainOwner = (owner: Owner): boolean => owner.isPotentialOwner === false;

  const getActionColumn = (owner: Owner) => {
    if (!isCertainOwner(owner)) {
      return (
        <div className="text-xs text-gray-500">
          Relance disponible uniquement pour les propriétaires certains
        </div>
      );
    }

    const eligible = isFollowUpEligible(owner);
    const alreadySent = hasFollowUpBeenSent(owner);
    const hasEmail = Boolean(owner.email?.trim());

    if (alreadySent) {
      return (
        <div className="flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700">
          <CheckCircleIcon className="h-4 w-4 shrink-0" />
          Relance déjà envoyée
        </div>
      );
    }

    if (!eligible) {
      const consentDate = getConsentContactDate(owner);
      if (consentDate) {
        const daysSince = Math.floor((Date.now() - consentDate.getTime()) / (1000 * 60 * 60 * 24));
        const daysRemaining = FOLLOW_UP_DAYS_THRESHOLD - daysSince;
        return (
          <div className="text-xs text-gray-500">
            Relance possible dans {daysRemaining} jour{daysRemaining > 1 ? 's' : ''}
          </div>
        );
      }
      return null;
    }

    return (
      <Button
        onClick={() => handleFollowUpClick(owner)}
        className={`bg-rgpd-blue-light text-rgpd-white ${!hasEmail && 'cursor-not-allowed opacity-50'}`}
        disabled={!hasEmail}
        type="button"
      >
        <ArrowPathIcon className="mr-1 h-4 w-4" />
        Envoyer un mail de relance
      </Button>
    );
  };

  const getDpeInfo = (owner: Owner) => {
    return formatDpeDetailsFromOwner(owner, handleDetailClick, handleAdsClick);
  };

  const tableData = data.map((owner) => ({
    'Propriétaire': <OwnerInfos owner={owner} showEditButton={true} onEditClick={handleEditClick} />,
    'Propriété': <CoproLocalOwned owner={owner} />,
    'DPE': getDpeInfo(owner),
    'Dernier contact': getLastContactInfo(owner),
    'Actions': getActionColumn(owner),
  }));

  return (
    <div>
      <div className="transition-all duration-300">
        <MUITable
          data={tableData}
          headers={headers}
          disablePagination={false}
          apiPagination={apiPagination}
          totalCount={totalCount}
          onPageChange={onPageChange}
          currentPage={currentPage}
          currentRowsPerPage={currentRowsPerPage}
        />
      </div>

      {isEditFormOpen && selectedOwner && (
        <Popup
          isOpen={isEditFormOpen}
          onClose={handleEditClose}
          title="Modifier les informations du propriétaire"
          width="80%"
          maxWidth="1200px"
          height="auto"
          maxHeight="90vh"
        >
          <div className="mx-auto block w-full">
            <DpeOwnerForm
              ownerIndex={0}
              owner={selectedOwner}
              updateOwnerCallback={(updatedOwner) => {
                if (updatedOwner.id) {
                  onOwnerEdit(updatedOwner.id);
                  dispatch(updateOwner({ ownerId: updatedOwner.id, updatedOwner }));
                }
                handleEditClose();
              }}
            />
          </div>
        </Popup>
      )}

      <DpeDetailsPopup
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        dpeDetails={selectedDpeDetails}
      />

      {isEmailPreviewOpen && emailPreviewData.to && (
        <EmailPreview
          isOpen={isEmailPreviewOpen}
          onClose={() => setIsEmailPreviewOpen(false)}
          emailContent={emailPreviewData.content}
          emailSubject={emailPreviewData.subject}
          emailTo={emailPreviewData.to}
          viewOnly={true}
        />
      )}

      {isFollowUpPreviewOpen && followUpPreviewData.to && (
        <EmailPreview
          isOpen={isFollowUpPreviewOpen}
          onClose={handleFollowUpPreviewClose}
          emailContent={followUpPreviewData.content}
          emailSubject={followUpPreviewData.subject}
          emailTo={followUpPreviewData.to}
          onCopy={handleFollowUpAction}
          onOpenMailApp={handleFollowUpAction}
          onSendFromReferent={followUpPreviewData.canSendFromReferent ? handleFollowUpSendFromReferent : undefined}
          actionRegisteredMessage="Relance enregistrée. Vous pouvez fermer cette fenêtre."
        />
      )}

      <AdsModal {...adsModalProps} />
    </div>
  );
};

export default PendingTable;
